import { useCallback, useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { Switch } from '../../ui/Switch';
import { SkeletonBlock } from '../../ui/Skeleton';
import './ai.css';

type Category = 'chat' | 'inbox' | 'questions' | 'vault' | 'todos' | 'interests' | 'digest' | 'recap' | 'dates';

const CATEGORIES: { key: Category; label: string; note: string }[] = [
  { key: 'chat', label: 'Chat', note: 'Let the concierge read your shared thread' },
  { key: 'inbox', label: 'Inbox', note: 'Let the concierge read your couple inbox' },
  { key: 'questions', label: 'Questions', note: 'Let the concierge read daily answers after reveal' },
  { key: 'vault', label: 'Vault', note: 'Let the concierge read saved links' },
  { key: 'todos', label: 'To-dos', note: 'Let the concierge manage shared to-dos' },
  // Last on purpose: walks and probes index this list from the top.
  { key: 'interests', label: 'Interests', note: 'Let the concierge word the topics in your interests profile' },
  { key: 'digest', label: 'Partner digest', note: 'Let the concierge write your weekly digest in Us' },
  // The weekly recap (card 3UwJvUtiirGN) — the same switch Settings flips.
  { key: 'recap', label: 'Weekly recap', note: 'Let the concierge write your weekly recap email' },
  // Date ideas (card qyhVbyMpcN0B) — appended dead last so the list order
  // every walk and probe indexes from the top stays what it was.
  { key: 'dates', label: 'Date ideas', note: 'Let the concierge propose date ideas from your interests and answers' },
];

interface LogEntry {
  id: string;
  category: Category;
  allowed: boolean;
  toggleState: boolean;
  reason: string;
  at: number;
}

/**
 * AI access panel — per-category toggles (all off by default) plus the recent
 * access log, ported from `ai/ui.ts`.
 *
 * Same endpoints and same PUT semantics; the markup is JSX now, so there is no
 * `innerHTML` rebuild per toggle and no button element recreated under the
 * user's finger mid-click.
 */
export function AIPanel() {
  const [toggles, setToggles] = useState<Record<Category, boolean> | null>(null);
  const [entries, setEntries] = useState<LogEntry[] | null>(null);
  const [busyKey, setBusyKey] = useState<Category | null>(null);
  const [failed, setFailed] = useState(false);

  const loadLog = useCallback(async () => {
    try {
      const data = await api<{ entries: LogEntry[] }>('/api/ai/log?limit=10');
      setEntries(data.entries ?? []);
    } catch (err) {
      console.error('[Pear] ai log failed', err);
      setEntries(prev => prev ?? []);
    }
  }, []);

  const loadToggles = useCallback(async () => {
    try {
      const data = await api<{ toggles: Record<Category, boolean> }>('/api/ai/toggles');
      setToggles(data.toggles);
      setFailed(false);
      return data.toggles;
    } catch (err) {
      console.error('[Pear] toggles load failed', err);
      setFailed(true);
      return null;
    }
  }, []);

  useEffect(() => {
    void loadToggles().then(current => {
      if (current) void loadLog();
    });
  }, [loadToggles, loadLog]);

  const toggle = async (cat: Category, on: boolean) => {
    if (busyKey) return;
    setBusyKey(cat);
    try {
      const data = await api<{ toggles: Record<Category, boolean> }>('/api/ai/toggles', {
        method: 'PUT',
        body: { category: cat, on: !on },
      });
      setToggles(data.toggles);
      await loadLog();
    } catch (err) {
      console.error('[Pear] toggle failed', err);
    } finally {
      setBusyKey(null);
    }
  };

  /** Master switch (card hF6aXvFC0ZyN): one request flips every category.
   *  All-off asks first only when a permission is actually on — confirming
   *  an all-off that changes nothing is friction, not safety. */
  const [masterBusy, setMasterBusy] = useState(false);
  const setAll = async (on: boolean) => {
    if (masterBusy) return;
    const anyOn = Object.values(toggles ?? {}).some(Boolean);
    if (!on && anyOn
      && !window.confirm('Turn off every concierge permission? Both of you lose access until one is switched back on.')) {
      return;
    }
    setMasterBusy(true);
    try {
      const data = await api<{ toggles: Record<Category, boolean> }>('/api/ai/toggles', {
        method: 'PUT',
        body: { all: on },
      });
      setToggles(data.toggles);
      await loadLog();
    } catch (err) {
      console.error('[Pear] master toggle failed', err);
    } finally {
      setMasterBusy(false);
    }
  };

  /* Card nLXV7fQuxbdl (Jon 2026-10-25: "the all on all off toggle doesn't
     reflect what's been toggled"): both master buttons are now driven by the
     toggles we already fetched — the one whose condition holds reads as
     pressed + disabled, so the master block states the truth instead of
     offering two identical idle buttons. Counts come from the same object the
     per-category .ai-group-count reads, so the two can never disagree. */
  const onCount = toggles ? CATEGORIES.filter(cat => toggles[cat.key] === true).length : 0;
  const allOn = toggles !== null && onCount === CATEGORIES.length;
  const allOff = toggles !== null && onCount === 0;
  const masterState = onCount === CATEGORIES.length
    ? 'All on'
    : onCount === 0 ? 'All off' : `${onCount} of ${CATEGORIES.length} on`;

  return (
    <div className="ai-panel">
      <h2 className="ai-title">AI access</h2>
      <p className="ai-lead">
        Everything starts off. The concierge can only read a category you switch on here — and every read is logged.
      </p>

      {toggles === null && !failed && (
        <div className="ai-list" aria-busy="true">
          <SkeletonBlock lines={3} />
        </div>
      )}

      {failed && (
        <p className="ai-error">Couldn’t load your toggles. Pull to retry or check back shortly.</p>
      )}

      {toggles && (
        <div className="ai-group">
          {/* Master switch (card hF6aXvFC0ZyN) — buttons, not Switch rows, so
              each per-category .pear-switch keeps its own identity for walks. */}
          <div className="ai-master" id="ai-master">
            <div className="ai-text">
              <div className="ai-label">All permissions</div>
              <div className="ai-note">
                Turn every category on at once — or off again. Each one stays adjustable below.
              </div>
              <div className="ai-master-state" id="ai-master-state" aria-live="polite">{masterState}</div>
            </div>
            <div className="ai-master-actions">
              <button
                type="button"
                id="btn-ai-all-on"
                className={`pear-btn${allOn ? ' pear-btn-mustard is-active' : ''}`}
                aria-pressed={allOn}
                disabled={masterBusy || allOn}
                onClick={() => void setAll(true)}
              >
                All on
              </button>
              <button
                type="button"
                id="btn-ai-all-off"
                className={`pear-btn${allOff ? ' pear-btn-mustard is-active' : ''}`}
                aria-pressed={allOff}
                disabled={masterBusy || allOff}
                onClick={() => void setAll(false)}
              >
                All off
              </button>
            </div>
          </div>
          <div className="ai-group-head">
            <span className="ai-group-title">What the concierge may read</span>
            <span className="ai-group-count">
              {Object.values(toggles).filter(Boolean).length} of {CATEGORIES.length} on
            </span>
          </div>
          <div className="ai-list">
            {CATEGORIES.map(cat => {
              const on = toggles[cat.key] === true;
              const busy = busyKey === cat.key;
              return (
                <div className="ai-row" key={cat.key}>
                  <div className="ai-text">
                    <div className="ai-label">{cat.label}</div>
                    <div className="ai-note">{cat.note}</div>
                  </div>
                  <Switch
                    checked={on}
                    busy={busy}
                    label={`${cat.label} access`}
                    disabled={busyKey !== null || masterBusy}
                    onChange={() => void toggle(cat.key, on)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* The two-path agent control moved to the Settings screen
          (card 0xhNPdS42FTh): permissions stay here, behaviour control lives
          with the rest of the settings. */}

      <div className="ai-log">
        <h3 className="ai-log-title">Recent reads</h3>
        {entries === null && <SkeletonBlock lines={2} />}
        {entries !== null && entries.length === 0 && <p className="ai-log-empty">No concierge reads yet.</p>}
        {entries?.map(e => (
          <div className="ai-log-row" key={e.id}>
            <span>
              {e.allowed ? 'allowed' : 'denied'} · {e.category} — {e.reason}
            </span>
            <span className="ai-log-time">{new Date(e.at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
